import { i18n } from '@vestico/i18n';

export const BraSizeSourceIDs = ['us', 'gb', 'eu', 'fr', 'es', 'it', 'jp', 'ko', 'au', 'nz'] as const;
export type BraSizeSourceID = (typeof BraSizeSourceIDs)[number];

const standardizedBandSizeStart = 57.5; // in cm
const standardizedBandSizeStep = 5.0; // in cm
export const BraSizeCupDiffDefault = 2.54;
export const BraSizeBandDiffDefault = standardizedBandSizeStep;

const CupSizeValueSkip = '_';

export interface BandSize {
  displayValue: string;
  standardizedValue: number;
}

export interface CupSize {
  displayValue: string;
  standardizedValue: number;
}

export interface BraSize {
  band: BandSize;
  cup: CupSize;
  sourceID: BraSizeSourceID;
}

export interface BraSizeSource {
  id: BraSizeSourceID;
  imageURL: string;
  bandSizes: BandSize[];
  cupSizes: CupSize[];
}

// Calculators

function GenerateBandSizes(startDisplayValue: number, maxDisplayValue: number, displayStep: number): BandSize[] {
  let standardizedBandSize = standardizedBandSizeStart;
  const bandSizes: BandSize[] = [];
  for (let index = startDisplayValue; index <= maxDisplayValue; index += displayStep) {
    const bandSize = {
      displayValue: String(index),
      standardizedValue: standardizedBandSize,
    };
    bandSizes.push(bandSize);

    standardizedBandSize += standardizedBandSizeStep;
  }
  return bandSizes;
}

//
// standardizedStartSize: in cm
// stepSize: in cm
//
function GenerateCupSizes(standardizedStartSize: number, stepSize: number, displayValues: string[]): CupSize[] {
  let standardizedCupSize = standardizedStartSize;
  const cupSizes: CupSize[] = [];
  for (const cupSizeDisplayValue of displayValues) {
    if (cupSizeDisplayValue === CupSizeValueSkip) {
      continue;
    }

    const cupSize = {
      displayValue: cupSizeDisplayValue,
      standardizedValue: standardizedCupSize,
    };
    cupSizes.push(cupSize);

    standardizedCupSize += stepSize;
  }

  return cupSizes;
}

export const BraSizeSourceUS = () => {
  const bandSizes: BandSize[] = bandSizesUS_UK();
  const cupSizeDisplayValues = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G', 'H', 'I', 'J', CupSizeValueSkip, 'K', 'L', 'M'];
  const cupSizes: CupSize[] = GenerateCupSizes(8.89, BraSizeCupDiffDefault, cupSizeDisplayValues);

  const braSizeSource: BraSizeSource = {
    id: 'us',
    imageURL: '🇺🇸',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };

  return braSizeSource;
};

export const BraSizeSourceUK = () => {
  const bandSizes: BandSize[] = bandSizesUS_UK();
  const cupSizeDisplayValues = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG', CupSizeValueSkip, 'H', 'HH', 'J'];
  const cupSizes: CupSize[] = GenerateCupSizes(8.89, BraSizeCupDiffDefault, cupSizeDisplayValues);

  const braSizeSource: BraSizeSource = {
    id: 'gb',
    imageURL: '🇬🇧',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };

  return braSizeSource;
};

const BraSizeSourceEU = () => {
  const bandSizes: BandSize[] = GenerateBandSizes(55, 150, 5);
  const cupSizes: CupSize[] = cupSizesEU_FR_ES_BE_IT();

  const braSizeSource: BraSizeSource = {
    id: 'eu',
    imageURL: '🇪🇺',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceFR = () => {
  const bandSizes: BandSize[] = bandSizesFR_ES_BE();
  const cupSizes: CupSize[] = cupSizesEU_FR_ES_BE_IT();

  const braSizeSource: BraSizeSource = {
    id: 'fr',
    imageURL: '🇫🇷',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceES = () => {
  const bandSizes: BandSize[] = bandSizesFR_ES_BE();
  const cupSizes: CupSize[] = cupSizesEU_FR_ES_BE_IT();

  const braSizeSource: BraSizeSource = {
    id: 'es',
    imageURL: '🇪🇸',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceIT = () => {
  const bandSizes: BandSize[] = GenerateBandSizes(-1, 18, 1);

  // manually manipulate italian band size: -1 => 00
  const startBandSize = bandSizes[0];
  startBandSize.displayValue = '00';
  bandSizes[0] = startBandSize;
  const cupSizes: CupSize[] = cupSizesEU_FR_ES_BE_IT();

  const braSizeSource: BraSizeSource = {
    id: 'it',
    imageURL: '🇮🇹',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceAU = () => {
  const bandSizes: BandSize[] = bandSizesAU_NZ();
  const cupSizes: CupSize[] = cupSizesAU_NZ();

  const braSizeSource: BraSizeSource = {
    id: 'au',
    imageURL: '🇦🇺',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceNZ = () => {
  const bandSizes: BandSize[] = bandSizesAU_NZ();
  const cupSizes: CupSize[] = cupSizesAU_NZ();

  const braSizeSource: BraSizeSource = {
    id: 'nz',
    imageURL: '🇳🇿',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceJP = () => {
  const bandSizes: BandSize[] = bandSizesJP_KO();
  const cupSizes: CupSize[] = cupSizesJP_KO();

  const braSizeSource: BraSizeSource = {
    id: 'jp',
    imageURL: '🇯🇵',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

const BraSizeSourceKO = () => {
  const bandSizes: BandSize[] = bandSizesJP_KO();
  const cupSizes: CupSize[] = cupSizesJP_KO();

  const braSizeSource: BraSizeSource = {
    id: 'ko',
    imageURL: '🇰🇷',
    bandSizes: bandSizes,
    cupSizes: cupSizes,
  };
  return braSizeSource;
};

// Helpers

// eslint-disable-next-line camelcase
function bandSizesUS_UK(): BandSize[] {
  return GenerateBandSizes(26, 64, 2);
}

// eslint-disable-next-line camelcase
function bandSizesFR_ES_BE(): BandSize[] {
  return GenerateBandSizes(70, 165, 5);
}

// eslint-disable-next-line camelcase
function bandSizesAU_NZ(): BandSize[] {
  return GenerateBandSizes(4, 42, 2);
}

// eslint-disable-next-line camelcase
function bandSizesJP_KO(): BandSize[] {
  return GenerateBandSizes(55, 150, 5);
}

// eslint-disable-next-line camelcase
function cupSizesEU_FR_ES_BE_IT(): CupSize[] {
  const cupSizeDisplayValues = ['AA', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'];
  const cupSizes: CupSize[] = GenerateCupSizes(11, 2, cupSizeDisplayValues);
  return cupSizes;
}

// eslint-disable-next-line camelcase
function cupSizesAU_NZ(): CupSize[] {
  const cupSizeDisplayValues = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];
  const cupSizes: CupSize[] = GenerateCupSizes(11, 2, cupSizeDisplayValues);
  return cupSizes;
}

// eslint-disable-next-line camelcase
function cupSizesJP_KO(): CupSize[] {
  const cupSizeDisplayValues = ['AA', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', CupSizeValueSkip, 'K', 'L', 'M'];
  const cupSizes: CupSize[] = GenerateCupSizes(8.89, BraSizeCupDiffDefault, cupSizeDisplayValues);
  return cupSizes;
}

const createBraSizeSources = () => {
  return [
    BraSizeSourceAU(),
    BraSizeSourceES(),
    BraSizeSourceEU(),
    BraSizeSourceFR(),
    BraSizeSourceIT(),
    BraSizeSourceJP(),
    BraSizeSourceKO(),
    BraSizeSourceNZ(),
    BraSizeSourceUK(),
    BraSizeSourceUS(),
  ];
};

export const titleForBraSizeSource = (braSizeSource: BraSizeSource) => {
  switch (braSizeSource.id) {
    case 'us':
      return i18n.t('LOC_BRA_SIZE_OPTION_US', { ns: 'model' });
    case 'gb':
      return i18n.t('LOC_BRA_SIZE_OPTION_GB', { ns: 'model' });
    case 'eu':
      return i18n.t('LOC_BRA_SIZE_OPTION_EU', { ns: 'model' });
    case 'fr':
      return i18n.t('LOC_BRA_SIZE_OPTION_FR', { ns: 'model' });
    case 'es':
      return i18n.t('LOC_BRA_SIZE_OPTION_ES', { ns: 'model' });
    case 'it':
      return i18n.t('LOC_BRA_SIZE_OPTION_IT', { ns: 'model' });
    case 'jp':
      return i18n.t('LOC_BRA_SIZE_OPTION_JP', { ns: 'model' });
    case 'ko':
      return i18n.t('LOC_BRA_SIZE_OPTION_KO', { ns: 'model' });
    case 'au':
      return i18n.t('LOC_BRA_SIZE_OPTION_AU', { ns: 'model' });
    case 'nz':
      return i18n.t('LOC_BRA_SIZE_OPTION_NZ', { ns: 'model' });
  }
  throw new Error(`Unhandled braSizeSource: ${braSizeSource.id}`);
};

// eslint-disable-next-line no-underscore-dangle
let _sources: BraSizeSource[] | undefined;
// eslint-disable-next-line no-underscore-dangle
let _sortedSources: BraSizeSource[] | undefined;

export const BraSizeSources = () => {
  if (!_sources) {
    _sources = createBraSizeSources();
  }
  return _sources;
};

export const BraSizeSourcesSorted = () => {
  if (!_sortedSources) {
    const sortedSources = BraSizeSources();
    const titleForSource = new Map<string, string>();
    sortedSources.forEach((s) => titleForSource.set(s.id, titleForBraSizeSource(s)));
    // skipcq JS-0339
    _sortedSources = sortedSources.sort((a, b) => titleForSource.get(a.id)!.localeCompare(titleForSource.get(b.id)!));
  }
  return _sortedSources;
};

export function convertStandardizedBraSizeToUK(standardizedBandSize: number, standardizedCupSize: number): BraSize {
  const braSizeSourceUK = BraSizeSourceUK();
  return convertStandardizedBraSize(braSizeSourceUK, standardizedBandSize, standardizedCupSize);
}

export function convertStandardizedBraSize(braSizeSource: BraSizeSource, standardizedBandSize: number, standardizedCupSize: number): BraSize {
  let closestBandSize: BandSize | undefined;
  let closestBandSizeDiff = Number.MAX_VALUE;
  for (const bandSize of braSizeSource.bandSizes) {
    const diff = Math.abs(bandSize.standardizedValue - standardizedBandSize);
    if (!closestBandSize || diff < closestBandSizeDiff) {
      closestBandSize = bandSize;
      closestBandSizeDiff = diff;
    } else {
      // stop early when we know the diffs will only go up from here
      break;
    }
  }

  let closestCupSize: CupSize | undefined;
  let closestCupSizeDiff = Number.MAX_VALUE;
  for (const cupSize of braSizeSource.cupSizes) {
    const diff = Math.abs(cupSize.standardizedValue - standardizedCupSize);
    if (!closestCupSize || diff < closestCupSizeDiff) {
      closestCupSize = cupSize;
      closestCupSizeDiff = diff;
    } else {
      // stop early when we know the diffs will only go up from here
      break;
    }
  }

  if (!closestBandSize || !closestCupSize) {
    throw new Error(`Unexpected error: Cannot convert to standard bra size`);
  }

  return {
    band: closestBandSize,
    cup: closestCupSize,
    sourceID: braSizeSource.id,
  };
}

export function findBraSizeIndex(
  braSizeSource: BraSizeSource,
  standardizedBandSize: number,
  standardizedCupSize: number
): [bandIndex: number, cupIndex: number] {
  let closestBandSizeIdx: number | undefined;
  let closestBandSizeDiff = Number.MAX_VALUE;
  const { bandSizes, cupSizes } = braSizeSource;
  for (let i = 0; i < bandSizes.length; i++) {
    const bandSize = bandSizes[i];
    const diff = Math.abs(bandSize.standardizedValue - standardizedBandSize);
    if (!closestBandSizeIdx || diff < closestBandSizeDiff) {
      closestBandSizeIdx = i;
      closestBandSizeDiff = diff;
    } else {
      // stop early when we know the diffs will only go up from here
      break;
    }
  }

  let closestCupSizeIdx: number | undefined;
  let closestCupSizeDiff = Number.MAX_VALUE;
  for (let i = 0; i < cupSizes.length; i++) {
    const cupSize = cupSizes[i];
    const diff = Math.abs(cupSize.standardizedValue - standardizedCupSize);
    if (!closestCupSizeIdx || diff < closestCupSizeDiff) {
      closestCupSizeIdx = i;
      closestCupSizeDiff = diff;
    } else {
      // stop early when we know the diffs will only go up from here
      break;
    }
  }

  if (!closestBandSizeIdx || !closestCupSizeIdx) {
    throw new Error(`Unexpected error: Cannot convert to standard bra size`);
  }

  return [closestBandSizeIdx, closestCupSizeIdx];
}

export const findBandSizeForSource = (braSizeSource: BraSizeSource, bandSize: number): BandSize | undefined => {
  return braSizeSource.bandSizes.find((currentBandSize) => {
    return currentBandSize.displayValue === bandSize.toString(10);
  });
};

export const findCupSizeForSource = (braSizeSource: BraSizeSource, cupSize: string): CupSize | undefined => {
  return braSizeSource.cupSizes.find((currentCupSize) => {
    return currentCupSize.displayValue.toLowerCase() === cupSize.toLowerCase();
  });
};

export const BraSizeSourceForID = (id: BraSizeSourceID) => {
  for (const source of BraSizeSources()) {
    if (source.id === id) {
      return source;
    }
  }
  throw new Error(`Cannot find source for id ${id}`);
};
